import MonolithNotesAPI from "@/api/Notes";
import { usePermissions } from "@/hooks/usePermissions";
import { Button, TextInput, styled } from "@monolith-forensics/monolith-ui";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { NoteEditor } from "./NoteEditor";
import { useDebouncedCallback } from "use-debounce";
import shortUUID from "short-uuid";
import moment from "moment";
import { TimelineEvent } from "../../TimelineEvents.types";
import LoggingAPI from "@/api/logging";
import { NotesList } from "./NotesList";
import { Note } from "@/Features";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 0px;
`;

export const FlyoutNotesTab: React.FC<{
  defaultEventData: TimelineEvent;
}> = ({ defaultEventData }) => {
  const { currentUser } = usePermissions();
  const [search, setSearch] = useState("");
  const [creatingNote, setCreatingNote] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note>();
  const [currentView, setCurrentView] = useState("note-list");

  const createNoteMutation = useMutation({
    mutationFn: MonolithNotesAPI.createNote,
    onMutate: async () => {
      setCreatingNote(true);
    },
    onSuccess: (data, variables, context) => {
      LoggingAPI.createLog({
        case_id: defaultEventData.case.case_id,
        user_id: currentUser.user_id,
        message: `Created timeline event note.`,
        event: "update_timeline_event",
        object_type: "timeline_event",
        object_uuid: defaultEventData.uuid,
      });

      LoggingAPI.createLog({
        case_id: defaultEventData.case.case_id,
        user_id: currentUser.user_id,
        message: `Created case note.`,
        event: "create_case_note",
        object_type: "case_note",
        object_uuid: variables.uuid,
      });

      setCreatingNote(false);
      setSelectedNote(variables);
      setCurrentView("note-editor");
    },
  });

  const handleCreateNote = async () => {
    setCreatingNote(true);
    const note_uuid = shortUUID.uuid();
    const note_tag = `Timeline Note_${moment().toISOString()}`;

    const newNoteData = {
      uuid: note_uuid,
      case_id: defaultEventData.case.case_id,
      case_uuid: defaultEventData.case.uuid,
      linked_case: {
        case_id: defaultEventData.case.case_id,
        uuid: defaultEventData.case.uuid,
      },
      note_tag,
      path: note_tag,
      created_on: moment().toISOString(),
      created_by_id: currentUser.user_id,
      parent_id: null,
      is_folder: 0,
      object_type: "timeline_event",
      object_id: defaultEventData.uuid,
    };

    createNoteMutation.mutate(newNoteData);
  };

  const handleSelectNote = (note: Note) => {
    setSelectedNote(note);
    setCurrentView("note-editor");
  };

  const handleCloseEditor = () => {
    setSelectedNote(undefined);
    setCurrentView("note-list");
  };

  const debouncedHandleSearch = useDebouncedCallback((e) => {
    setSearch(e.target.value);
  }, 400);

  return (
    <StyledContainer className={"timeline-notes-tab"}>
      {currentView === "note-list" && (
        <>
          <div
            style={{
              display: "flex",
              flex: "initial",
              flexDirection: "row",
              gap: 5,
              alignContent: "center",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
              marginTop: 10,
              width: "100%",
            }}
          >
            <Button
              size="xxs"
              variant="contained"
              color="primary"
              onClick={handleCreateNote}
              loading={creatingNote}
            >
              + New Note
            </Button>
            <TextInput
              stylingMode="outlined"
              placeholder="Search Notes"
              height="100%"
              width={200}
              style={{ marginLeft: "auto" }}
              onKeyUp={debouncedHandleSearch}
            />
          </div>
          <NotesList
            defaultEventData={defaultEventData}
            onNoteSelected={handleSelectNote}
            search={search}
          />
        </>
      )}
      {currentView === "note-editor" && selectedNote && (
        <NoteEditor
          defaultNoteData={selectedNote}
          onClose={handleCloseEditor}
        />
      )}
    </StyledContainer>
  );
};
